import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useUnits } from "../lib/useUnits";
import { useToast } from "../lib/toast";
import { AdjustmentHistorySheet } from "../components/AdjustmentHistorySheet";
import { CyclingSheet } from "../components/CyclingSheet";
import { MealSplitSheet } from "../components/MealSplitSheet";
import { Button } from "../components/ui/Button";
import { ListSkeleton } from "../components/ui/Skeleton";
import { PageHeader } from "../components/ui/PageHeader";
import type { Goals } from "@shared/types";

export function MoreGoals() {
  const t = useT();
  const units = useUnits();
  const toast = useToast();
  const [g, setG] = useState<Goals | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showCycling, setShowCycling] = useState(false);
  const [showSplit, setShowSplit] = useState(false);

  useEffect(() => { api.getGoals().then(setG); }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!g) return;
    setErr(null); setMsg(null);
    try {
      await api.putGoals({
        daily_calories: g.daily_calories,
        maintenance_calories: g.maintenance_calories,
        protein_g: g.protein_g,
        target_weight_kg: g.target_weight_kg,
        step_target: g.step_target,
        activity_kcal_target: g.activity_kcal_target,
        water_target_ml: g.water_target_ml,
      });
      toast.success(t("toast.saved"));
      setMsg(t("more_goals.saved"));
    } catch (e) { setErr((e as Error).message); }
  }

  async function toggleAuto() {
    if (!g) return;
    const next = !g.auto_adjust_enabled;
    setG({ ...g, auto_adjust_enabled: next });
    try {
      await api.putGoals({ auto_adjust_enabled: next });
    } catch (e) {
      // revert on error
      setG({ ...g, auto_adjust_enabled: !next });
      setErr((e as Error).message);
    }
  }

  if (!g) return <ListSkeleton rows={5} />;

  const row = (label: string, k: keyof Goals, step = 1, mode?: "energy" | "weight" | "volume") => {
    // Units convert at the row boundary; `g` stays canonical throughout.
    const toD = (n: number) =>
      mode === "energy" ? units.energy(n).value
      : mode === "weight" ? units.weightParts(n).value
      : mode === "volume" ? units.volume(n).value
      : n;
    const fromD = (n: number) =>
      mode === "energy" && units.prefs.energy === "kj" ? Math.round(n / 4.184)
      : mode === "weight" ? Math.round(units.parseWeight(n) * 10) / 10
      : mode === "volume" && units.prefs.serving === "imperial" ? Math.round(n * 29.5735295625)
      : n;
    const suffix =
      mode === "energy" ? ` (${units.energy(0).unit})`
      : mode === "weight" ? ` (${units.weightParts(0).unit})`
      : mode === "volume" ? ` (${units.volume(0).unit})`
      : "";
    return (
      <GoalRow
        label={`${label}${suffix}`}
        step={step}
        value={g[k] == null ? null : toD(g[k] as number)}
        onChange={(n) => setG({ ...g, [k]: fromD(n) })}
      />
    );
  };

  return (
    <form onSubmit={save} className="space-y-4 pt-4 animate-rise">
      <PageHeader back="history" title={t("more_goals.title")} />

      <div className="flex items-center justify-between p-4 bg-surface rounded-2xl">
        <div>
          <div className="text-sm font-medium">{t("goals.auto_adjust")}</div>
          <div className="text-sm text-fg-muted mt-1">{t("goals.auto_adjust_hint")}</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={g.auto_adjust_enabled}
          aria-label={t("goals.auto_adjust")}
          onClick={toggleAuto}
          className={`w-12 h-7 rounded-full p-0.5 transition ${g.auto_adjust_enabled ? "bg-success" : "bg-surface-strong"}`}
        >
          <span aria-hidden="true" className={`block w-6 h-6 bg-primary rounded-full transition-transform ${g.auto_adjust_enabled ? "translate-x-5" : ""}`} />
        </button>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-2">
        <button
          type="button"
          onClick={() => setShowHistory(true)}
          className="text-sm text-fg-secondary underline min-h-[44px]"
        >
          {t("goals.see_adjustment_history")}
        </button>
        <button
          type="button"
          onClick={() => setShowCycling(true)}
          className="text-sm text-fg-secondary underline min-h-[44px]"
        >
          {t("cycling.title")}
        </button>
        <button
          type="button"
          onClick={() => setShowSplit(true)}
          className="w-full bg-surface rounded-2xl px-4 py-3.5 text-left flex items-center justify-between press"
        >
          <span>{t("split.title")}</span>
          <span className="text-sm text-fg-muted">
            {g.split_breakfast != null
              ? `${g.split_breakfast}/${g.split_lunch}/${g.split_dinner}/${g.split_snack}`
              : t("split.automatic_short")}
          </span>
        </button>
      </div>

      <div className="bg-surface rounded-2xl px-4 py-1">
        {row(t("more_goals.daily_cal"), "daily_calories", 1, "energy")}
        {row(t("more_goals.maintenance_cal"), "maintenance_calories")}
        {row(t("more_goals.protein"), "protein_g")}
        {row(t("more_goals.target_weight"), "target_weight_kg", 0.1, "weight")}
        {row(t("more_goals.step_target"), "step_target", 500)}
        {row(t("more_goals.fiber_target"), "fiber_target_g", 1)}
        {row(t("more_goals.activity_burn"), "activity_kcal_target", 50, "energy")}
        {row(t("more_goals.water_target"), "water_target_ml", 250, "volume")}
      </div>

      {err && <p className="text-danger text-sm">{err}</p>}
      {msg && <p className="text-success text-sm">{msg}</p>}

      <Button type="submit" fullWidth>{t("more_goals.save")}</Button>

      {showSplit && g && (
        <MealSplitSheet
          goals={g}
          onSaved={(next) => setG({ ...g, ...next })}
          onClose={() => setShowSplit(false)}
        />
      )}
      {showCycling && (
        <CyclingSheet
          baselineKcal={g?.daily_calories ?? 2000}
          onSaved={() => { /* Today refetches on next visit */ }}
          onClose={() => setShowCycling(false)}
        />
      )}
      <AdjustmentHistorySheet
        open={showHistory}
        onClose={() => setShowHistory(false)}
      />
    </form>
  );
}

/**
 * One goal field. Lives at module level — defining it inside MoreGoals gave it
 * a new component identity on every keystroke, so React remounted the input
 * and iOS dropped focus (and the keyboard) after each character.
 * Keeps a local text buffer so partial input ("74.", "") doesn't snap to a
 * parsed number mid-typing; the buffer resyncs from the prop on outside
 * changes and on blur.
 */
function GoalRow({ label, value, step = 1, onChange }: {
  label: string;
  value: number | null;
  step?: number;
  onChange: (n: number) => void;
}) {
  const [text, setText] = useState(value == null ? "" : String(value));

  useEffect(() => {
    const n = parseFloat(text.replace(",", "."));
    if (!(Number.isFinite(n) && n === value)) setText(value == null ? "" : String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <label className="flex justify-between items-center py-3 border-b border-line last:border-b-0">
      <span className="text-fg-secondary">{label}</span>
      <input
        type="number" step={step} inputMode="decimal"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const n = parseFloat(e.target.value.replace(",", "."));
          if (Number.isFinite(n) && n >= 0) onChange(n);
        }}
        onBlur={() => setText(value == null ? "" : String(value))}
        className="input w-28 text-right tabular-nums"
      />
    </label>
  );
}
